import { useTranslations } from '@vocab/react';
import {
  Stack,
  Heading,
  Actions,
  Button,
  IconDelete,
  Text,
  Card,
} from 'braid-design-system';
import { useState } from 'react';
import { useNavigate } from 'react-router';

import translations from 'src/App/Components/RemoveUserDialog/.vocab';
import {
  RemoveUserDialog,
  type UserToRemove,
} from 'src/App/Components/RemoveUserDialog/RemoveUserDialog';
import { BASE_URL } from 'src/constants/urls';
import type { UserDetails } from 'src/graphql/generated';
import { useAccountContext } from 'src/hooks/use-account-context';
import { useUserActions } from 'src/hooks/use-user-actions';
import { useConfig } from 'src/modules/ConfigProvider';
import { trackClickDeleteUser } from 'src/utils/trackingUserActions';

interface RemoveUserFromAccountCardProps {
  userToEdit: UserDetails;
}

export const RemoveUserFromAccountCard = ({
  userToEdit,
}: RemoveUserFromAccountCardProps) => {
  const { data: accountData } = useAccountContext();
  const [userToRemove, setUserToRemove] = useState<UserToRemove | null>(null);
  const navigate = useNavigate();
  const { urlResolver } = useConfig();
  const { handleRemoveUser } = useUserActions();
  const { t } = useTranslations(translations);

  const removeUser = (user: UserToRemove) => {
    if (!userToRemove) {
      setUserToRemove(user);
      return;
    }
    if (user.id === userToRemove.id) {
      handleRemoveUser(Number(user.id));
      navigate(urlResolver({ path: `/${BASE_URL}` }));
    }
    setUserToRemove(null);
  };

  return (
    <>
      {userToEdit.id !== accountData?.headerFooterContext?.user?.id && (
        <Stack space="large">
          <Heading level="3">{t('Remove team member title')}</Heading>
          <Card>
            <Stack space="medium">
              <Text>
                {t('Remove team member description')}{' '}
                {accountData?.headerFooterContext?.advertiser?.name} (
                {accountData?.headerFooterContext?.advertiser?.billingId})
              </Text>

              <Actions>
                <Button
                  tone="critical"
                  icon={<IconDelete />}
                  onClick={() => {
                    trackClickDeleteUser(userToEdit.id);
                    setUserToRemove(userToEdit);
                  }}
                >
                  {t('Remove team member button')}
                </Button>
              </Actions>
            </Stack>
          </Card>
        </Stack>
      )}
      <RemoveUserDialog
        userToRemove={userToRemove}
        removeUser={removeUser}
        setUserToRemove={setUserToRemove}
      />
    </>
  );
};
